import type { SettingsEvent } from "@/features/settings/mediators/settingsEvents";
import { createMediator, type Mediator } from "@/shared/lib/componentHierarchy";

export type NgDraftField =
  | "displayId"
  | "title"
  | "word"
  | "regex"
  | "image"
  | "imageNote";
export type NgListField = Exclude<NgDraftField, "imageNote">;
export type NgSettingsEvent = Extract<
  SettingsEvent,
  {
    type:
      | "settings/ng-draft-changed"
      | "settings/ng-add-requested"
      | "settings/ng-remove-requested"
      | "settings/ng-clear-images-requested"
      | "settings/ng-image-add-completed"
      | "settings/ng-boolean-changed";
  }
>;

export function isNgSettingsEvent(
  event: SettingsEvent,
): event is NgSettingsEvent {
  return (
    event.type === "settings/ng-draft-changed" ||
    event.type === "settings/ng-add-requested" ||
    event.type === "settings/ng-remove-requested" ||
    event.type === "settings/ng-clear-images-requested" ||
    event.type === "settings/ng-image-add-completed" ||
    event.type === "settings/ng-boolean-changed"
  );
}

export type NgSettingsState = Readonly<Record<NgDraftField, string>>;

export type NgSettingsEffect =
  | {
      readonly type: "add";
      readonly field: NgListField;
      readonly value: string;
      readonly note?: string;
    }
  | {
      readonly type: "remove";
      readonly field:
        | "displayId"
        | "title"
        | "word"
        | "regex"
        | "image"
        | "hiddenThread";
      readonly value: string | number;
    }
  | {
      readonly type: "set-boolean";
      readonly field: "enabled" | "showNgContent";
      readonly value: boolean;
    }
  | { readonly type: "confirm-clear-images" }
  | { readonly type: "alert"; readonly message: string };

const EMPTY_STATE: NgSettingsState = {
  displayId: "",
  title: "",
  word: "",
  regex: "",
  image: "",
  imageNote: "",
};

const EMPTY_MESSAGES: Readonly<Record<NgListField, string>> = {
  displayId: "表示IDを入力してください",
  title: "NGスレ文を入力してください",
  word: "NGワードを入力してください",
  regex: "正規表現パターンを入力してください",
  image: "画像ハッシュを入力してください",
};

export function createNgSettingsMediator(
  runEffect: (effect: NgSettingsEffect) => void,
): Mediator<NgSettingsState, NgSettingsEvent, NgSettingsEffect> {
  return createMediator<NgSettingsState, NgSettingsEvent, NgSettingsEffect>(
    EMPTY_STATE,
    (state, event) => {
      switch (event.type) {
        case "settings/ng-draft-changed":
          return {
            status: "handled",
            state: { ...state, [event.field]: event.value },
          };
        case "settings/ng-remove-requested":
          return {
            status: "handled",
            state,
            effects: [
              { type: "remove", field: event.field, value: event.value },
            ],
          };
        case "settings/ng-clear-images-requested":
          return {
            status: "handled",
            state,
            effects: [{ type: "confirm-clear-images" }],
          };
        case "settings/ng-boolean-changed":
          return {
            status: "handled",
            state,
            effects: [
              {
                type: "set-boolean",
                field: event.field,
                value: event.value,
              },
            ],
          };
        case "settings/ng-image-add-completed":
          return {
            status: "handled",
            state:
              event.success &&
              state.image.trim() === event.value &&
              state.imageNote === event.note
                ? { ...state, image: "", imageNote: "" }
                : state,
            effects: [{ type: "alert", message: event.message }],
          };
        case "settings/ng-add-requested": {
          const value = state[event.field].trim();
          if (!value) {
            return {
              status: "handled",
              state,
              effects: [
                { type: "alert", message: EMPTY_MESSAGES[event.field] },
              ],
            };
          }
          if (event.field === "regex") {
            try {
              new RegExp(value);
            } catch {
              return {
                status: "handled",
                state,
                effects: [
                  { type: "alert", message: "無効な正規表現パターンです" },
                ],
              };
            }
          }
          if (event.field === "image") {
            return {
              status: "handled",
              state,
              effects: [
                {
                  type: "add",
                  field: "image",
                  value,
                  note: state.imageNote,
                },
              ],
            };
          }
          return {
            status: "handled",
            state: { ...state, [event.field]: "" },
            effects: [{ type: "add", field: event.field, value }],
          };
        }
      }
    },
    { runEffect },
  );
}
